import { useState, useCallback } from 'react';
import { postPicksVoid, groupPicks } from 'services/picking';
import { logErrorCtx } from 'app/logging';

export type PickingBulkActionKey = 'void' | 'group';

export interface UsePickingBulkActionsProps {
  selectedPickIds: number[];
  onActionSuccess: () => void; // Callback to refresh data and clear selections
  onActionError?: (error: any, actionType: PickingBulkActionKey) => void; // Optional error callback
}

export interface UsePickingBulkActionsReturn {
  executePickingBulkAction: (actionKey: PickingBulkActionKey) => Promise<void>;
  isLoading: boolean;
  error: any | null;
  isVoiding: boolean;
  isGrouping: boolean;
}

const pickingServiceMap: Record<
  PickingBulkActionKey,
  (ids: number[]) => Promise<void>
> = {
  void: postPicksVoid,
  group: groupPicks,
};

export function usePickingBulkActions({
  selectedPickIds,
  onActionSuccess,
  onActionError,
}: UsePickingBulkActionsProps): UsePickingBulkActionsReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any | null>(null);
  const [currentAction, setCurrentAction] =
    useState<PickingBulkActionKey | null>(null);

  const handleAction = useCallback(
    async (
      actionFn: (ids: number[]) => Promise<void>,
      actionType: PickingBulkActionKey
    ) => {
      if (selectedPickIds.length === 0) {
        const err = new Error(`No picks selected for bulk ${actionType}.`);
        setError(err);
        onActionError?.(err, actionType);
        return;
      }
      setCurrentAction(actionType);
      setIsLoading(true);
      setError(null);
      try {
        await actionFn(selectedPickIds);
        await onActionSuccess(); // Ensure this is awaited if it's async
      } catch (err) {
        const errorMessage = `Error during bulk ${actionType} picks`;
        logErrorCtx(errorMessage, {
          error: err instanceof Error ? err : new Error(String(err)),
          description: `Failed to ${actionType} ${selectedPickIds.length} picks`,
        });
        setError(err);
        onActionError?.(err, actionType);
      } finally {
        setIsLoading(false);
        setCurrentAction(null);
      }
    },
    [selectedPickIds, onActionSuccess, onActionError]
  );

  const executePickingBulkAction = useCallback(
    async (actionKey: PickingBulkActionKey) => {
      const serviceFunction = pickingServiceMap[actionKey];
      if (serviceFunction) {
        await handleAction(serviceFunction, actionKey);
      } else {
        const err = new Error(`Invalid picking action key: ${actionKey}`);
        logErrorCtx('Invalid picking action key', { error: err });
        setError(err);
        onActionError?.(err, actionKey);
      }
    },
    [handleAction, onActionError]
  );

  return {
    executePickingBulkAction,
    isLoading,
    error,
    isVoiding: isLoading && currentAction === 'void',
    isGrouping: isLoading && currentAction === 'group',
  };
}
